import { S3File, S3Folder, S3ListItem, ActiveFilter, StarType, CustomFolder } from '../types';
import {
  CATEGORY_TO_FASE_MAPPING,
  TOP_LEVEL_CATEGORIES,
  MACHINE_CODES,
  SAMPLE_DATES,
  IMAGE_FILE_EXTENSIONS,
  DESCRIPTION_FILE_EXTENSION,
  GENERATED_IMAGES_CATEGORY_NAME,
  GENERATED_IMAGES_FASE_PATH,
  EXPORTED_IMAGES_CATEGORY_NAME,
  EXPORTED_IMAGES_FASE_PATH,
  USER_CREATED_WORK_SPANISH_NAME,
  USER_EXPORTED_ART_SPANISH_NAME
} from '../constants';

// Access environment variables directly from process.env to avoid window.process TypeScript errors
const WORDPRESS_API_BASE_URL = process.env.REACT_APP_WORDPRESS_API_URL || "";
const USE_MOCK_DATA = !WORDPRESS_API_BASE_URL;

interface MockS3Level {
  folders: string[];
  files: string[];
}
interface MockS3Store {
  [path: string]: MockS3Level;
}

const MOCK_DESCRIPTIONS: Record<string, string> = {};
const MOCK_S3_STRUCTURE: MockS3Store = {};
const MOCK_STAR_DATA: Record<string, StarType> = {};

interface ParsedFilenameData {
  category: string;
  date: string;
  time: string;
  dateTime: string;
  serial: string;
  seed: string;
  artist: string;
  machineCodeRaw: string;
  parsedMachineCode: string;
  extension: string;
  originalFilename: string;
  shortDisplayName: string;
}

const FILENAME_PARSE_PATTERN = /^(.+?)_(\d{4}-\d{2}-\d{2}) (\d{2}-\d{2}-\d{2})_(\d{5})-(\d+)-(.+?)-(.+?)\.(jpg|jpeg|png)$/i;

const parseS3Filename = (filename: string, customFolderName?: string): ParsedFilenameData | null => {
  const match = filename.match(FILENAME_PARSE_PATTERN);
  if (match) {
    const category = match[1];
    const serial = match[4];
    let shortDisplayName = `${category} - ${serial}`;
    const machineCodeRaw = match[7];
    const parsedMachineCode = machineCodeRaw.startsWith("___") ? machineCodeRaw.substring(3) : machineCodeRaw;

    if (category.startsWith("Generated") || category.startsWith(GENERATED_IMAGES_CATEGORY_NAME)) {
        shortDisplayName = customFolderName 
            ? `${customFolderName.substring(0,10)}.. - ${serial}` 
            : `Generada - ${serial}`;
        if (category.includes("_")) {
            const editLabelPart = category.substring(category.indexOf("_") + 1).replace(/-/g, ' ');
            shortDisplayName = `Edit: ${editLabelPart} - ${serial}`;
             if (customFolderName) {
                shortDisplayName = `Edit: ${editLabelPart} (${customFolderName.substring(0,10)}..) - ${serial}`;
            }
        }
    } else if (category.startsWith("Exported") || category.startsWith(EXPORTED_IMAGES_CATEGORY_NAME)) {
      shortDisplayName = `Arte Final - ${serial}`;
    }
    if (shortDisplayName.length > 35) {
        shortDisplayName = shortDisplayName.substring(0, 32) + "...";
    }

    return {
      category: category,
      date: match[2],
      time: match[3],
      dateTime: `${match[2]} ${match[3]}`,
      serial: serial,
      seed: match[5],
      artist: match[6],
      machineCodeRaw: machineCodeRaw,
      parsedMachineCode: parsedMachineCode,
      extension: match[8],
      originalFilename: filename,
      shortDisplayName: shortDisplayName,
    };
  }
  return null;
};

const generateMockDescription = (filePath: string, imageName: string, date: string, machineWithPrefix: string, fase: string, category: string, customFolderName?: string): string => {
  const parsedData = parseS3Filename(imageName, customFolderName);
  let seed = "N/A";
  let steps = 20 + Math.floor(Math.random() * 10);
  let cfg = 5 + Math.floor(Math.random() * 5);

  if (parsedData) {
    seed = parsedData.seed;
  } else {
    seed = (Math.floor(Math.random() * 8999999999) + 1000000000).toString();
  }

  const samplers = ["Euler a", "DPM++ 2M Karras", "UniPC", "DDIM"];
  const sampler = samplers[Math.floor(Math.random() * samplers.length)];
  let description = `Image: ${imageName}\n`;
  description += `Category: ${category}, Fase: ${fase}, Machine: ${machineWithPrefix}, Date: ${date}\n`;
  if (customFolderName) {
    description += `Custom Folder: ${customFolderName}\n`;
  }
  description += `Prompt: a beautiful portrait of a character in ${category} style${customFolderName ? ` (folder: ${customFolderName})` : ''}, masterpiece, high quality, detailed.\n`;
  description += `Negative prompt: ugly, deformed, noisy, blurry, lowres, bad anatomy\n`;
  description += `Steps: ${steps}, Sampler: ${sampler}, CFG scale: ${cfg}, Seed: ${seed}, Size: 512x768, Model hash: abcdef1234, Model: customModel_v1, Version: v1.0.0\n`;
  description += `---\nFile path: ${filePath}\nThis is a mock description for ${imageName}.`;
  return description;
};

function initializeMockData() {
  if (!USE_MOCK_DATA) return;
  MOCK_S3_STRUCTURE[""] = { folders: TOP_LEVEL_CATEGORIES, files: [] };

  TOP_LEVEL_CATEGORIES.forEach(category => {
    const fasePath = CATEGORY_TO_FASE_MAPPING[category] + "/";
    MOCK_S3_STRUCTURE[fasePath] = { folders: [], files: [] }; 

    MACHINE_CODES.forEach(machineCodeWithPrefix => {
      const machinePath = fasePath + machineCodeWithPrefix + "/";
      MOCK_S3_STRUCTURE[machinePath] = { folders: [], files: [] };

      SAMPLE_DATES.forEach(date => {
        const datePath = machinePath + date + "/";
        const numImages = 2 + Math.floor(Math.random() * 4);
        const imageFiles: string[] = [];
        for (let i = 1; i <= numImages; i++) {
          const imgExtension = IMAGE_FILE_EXTENSIONS[Math.floor(Math.random() * IMAGE_FILE_EXTENSIONS.length)];
          const mockTime = `${String(i % 24).padStart(2, '0')}-${String((i * 5 + 3) % 60).padStart(2, '0')}-${String((i * 7 + 11) % 60).padStart(2, '0')}`;
          const mockSerialNumber = String(i).padStart(5, '0');
          const mockSeed = (Math.floor(Math.random() * 8999999999) + 1000000000).toString();
          const mockArtist = "www.arteai.eu"; 
          const machineCodeForFilename = machineCodeWithPrefix.startsWith("___") ? machineCodeWithPrefix.substring(3) : machineCodeWithPrefix;
          const imageName = `${category}_${date} ${mockTime}_${mockSerialNumber}-${mockSeed}-${mockArtist}-${machineCodeForFilename}${imgExtension}`;

          imageFiles.push(imageName);
          const descriptionFilePath = datePath + imageName.replace(/\.[^/.]+$/, "") + DESCRIPTION_FILE_EXTENSION;
          MOCK_DESCRIPTIONS[descriptionFilePath] = generateMockDescription(descriptionFilePath, imageName, date, machineCodeWithPrefix, CATEGORY_TO_FASE_MAPPING[category], category);
        
          if (Math.random() < 0.3) {
            const starValues = Object.values(StarType);
            MOCK_STAR_DATA[datePath + imageName] = starValues[Math.floor(Math.random() * starValues.length)];
          }
        }
        MOCK_S3_STRUCTURE[datePath] = { folders: [], files: imageFiles };
      });
    });
  });

  if (!MOCK_S3_STRUCTURE[GENERATED_IMAGES_FASE_PATH + "/"]) MOCK_S3_STRUCTURE[GENERATED_IMAGES_FASE_PATH + "/"] = { folders: [], files: [] };
  if (!MOCK_S3_STRUCTURE[EXPORTED_IMAGES_FASE_PATH + "/"]) MOCK_S3_STRUCTURE[EXPORTED_IMAGES_FASE_PATH + "/"] = { folders: [], files: [] };
}

initializeMockData();

export const listS3Items = async (
  currentPath: string,
  isUserGalleryMode: boolean = false,
  filterCriteria?: ActiveFilter,
  username?: string,
  customFoldersParam?: CustomFolder[]
): Promise<S3ListItem[]> => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve) => {
      setTimeout(() => {
        if (filterCriteria) {
          const filteredFiles: S3File[] = [];
          Object.keys(MOCK_S3_STRUCTURE).forEach(pathKey => {
            const levelData = MOCK_S3_STRUCTURE[pathKey];
            if (levelData && levelData.files.length > 0) {
              levelData.files.forEach(fileName => {
                const fileId = pathKey + fileName;
                let match = false;
                const pathParts = pathKey.split('/').filter(Boolean);
                let customFolderNameFromPath: string | undefined = undefined;
                if (pathParts.length === 4 && pathParts[0] === GENERATED_IMAGES_FASE_PATH && pathParts[1] === username) { 
                    customFolderNameFromPath = pathParts[2];
                }
                const parsedData = parseS3Filename(fileName, customFolderNameFromPath);

                if (filterCriteria.type === 'star') {
                  if (MOCK_STAR_DATA[fileId] === filterCriteria.value && filterCriteria.value !== StarType.None) match = true;
                } else if (parsedData) {
                    if (filterCriteria.type === 'date' && parsedData.date === filterCriteria.value) match = true;
                    else if (filterCriteria.type === 'artist' && parsedData.artist.toLowerCase() === filterCriteria.value.toLowerCase()) match = true;
                    else if (filterCriteria.type === 'machineCode' && parsedData.parsedMachineCode.toLowerCase() === filterCriteria.value.toLowerCase()) match = true;
                }
                
                if (match) {
                  const displayName = parsedData?.shortDisplayName || (fileName.length > 30 ? fileName.substring(0,27) + "..." : fileName);
                  filteredFiles.push({
                    itemType: 'file', id: fileId, name: fileName, displayName,
                    parsedDate: parsedData?.date, parsedTime: parsedData?.time, parsedMachineCode: parsedData?.parsedMachineCode,
                    url: MOCK_DESCRIPTIONS[fileId] && MOCK_DESCRIPTIONS[fileId].startsWith('data:image') ? MOCK_DESCRIPTIONS[fileId] : `https://picsum.photos/seed/${fileId.replace(/[^a-zA-Z0-9]/g, '')}/400/300`,
                    prefix: pathKey, descriptionTextPath: pathKey + fileName.replace(/\.[^/.]+$/, "") + DESCRIPTION_FILE_EXTENSION, 
                  });
                }
              });
            }
          });
          filteredFiles.sort((a, b) => {
            const parsedA = parseS3Filename(a.name);
            const parsedB = parseS3Filename(b.name);
            return (parsedA && parsedB) ? parsedB.dateTime.localeCompare(parsedA.dateTime) : a.name.localeCompare(b.name);
          });
          resolve(filteredFiles);
          return;
        }

        const results: S3ListItem[] = [];
        if ((currentPath === "" || currentPath === "filter_view" || currentPath === "filter_view_star")) { 
          if (isUserGalleryMode && username) {
            const userDraftsPath = `${GENERATED_IMAGES_FASE_PATH}/${username}/`;
            const userFinalArtPath = `${EXPORTED_IMAGES_FASE_PATH}/${username}/`;
            results.push({ itemType: 'folder', id: userDraftsPath, name: USER_CREATED_WORK_SPANISH_NAME, path: userDraftsPath });
            results.push({ itemType: 'folder', id: userFinalArtPath, name: USER_EXPORTED_ART_SPANISH_NAME, path: userFinalArtPath });
            if (customFoldersParam) {
              customFoldersParam.forEach(cf => {
                const customFolderPath = `${GENERATED_IMAGES_FASE_PATH}/${username}/${cf.name}/`;
                results.push({ itemType: 'folder', id: customFolderPath, name: cf.name, path: customFolderPath });
              });
            }
          } else if (!isUserGalleryMode) {
            TOP_LEVEL_CATEGORIES.forEach(categoryName => {
              results.push({ itemType: 'folder', id: CATEGORY_TO_FASE_MAPPING[categoryName] + "/", name: categoryName, path: CATEGORY_TO_FASE_MAPPING[categoryName] + "/" });
            });
          }
          resolve(results.sort((a,b) => a.name.localeCompare(b.name)));
          return;
        }

        const pathParts = currentPath.split('/').filter(Boolean);
        const pathWithoutTrailingSlash = currentPath.endsWith('/') ? currentPath.slice(0, -1) : currentPath;
        const isBasePublicCategoryPath = TOP_LEVEL_CATEGORIES.some(catKey => CATEGORY_TO_FASE_MAPPING[catKey] === pathWithoutTrailingSlash) && currentPath.endsWith('/') && !isUserGalleryMode;

        if (isBasePublicCategoryPath) {
          const uniqueDates = new Set<string>();
          MACHINE_CODES.forEach(machineCode => { 
            SAMPLE_DATES.forEach(date => { 
              const fullOriginalDatePath = currentPath + machineCode + "/" + date + "/"; 
              if (MOCK_S3_STRUCTURE[fullOriginalDatePath] && MOCK_S3_STRUCTURE[fullOriginalDatePath].files.length > 0) uniqueDates.add(date);
            });
          });
          uniqueDates.forEach(date => results.push({ itemType: 'folder', id: currentPath + date + "/", name: date, path: currentPath + date + "/" }));
          resolve(results.sort((a,b) => b.name.localeCompare(a.name))); 
          return;
        }

        const isPublicCategoryDatePath = pathParts.length === 2 && TOP_LEVEL_CATEGORIES.some(catKey => CATEGORY_TO_FASE_MAPPING[catKey] === pathParts[0]) && !isUserGalleryMode;
        if (isPublicCategoryDatePath) {
          const baseFasePath = pathParts[0] + "/"; 
          const targetDate = pathParts[1]; 
          MACHINE_CODES.forEach(machineCode => {
            const originalDatePath = baseFasePath + machineCode + "/" + targetDate + "/"; 
            const levelData = MOCK_S3_STRUCTURE[originalDatePath];
            if (levelData) {
              levelData.files.forEach(fileName => {
                const parsedData = parseS3Filename(fileName);
                results.push({
                  itemType: 'file', id: originalDatePath + fileName, name: fileName,
                  displayName: parsedData?.shortDisplayName || (fileName.length > 30 ? fileName.substring(0,27) + "..." : fileName),
                  parsedDate: parsedData?.date, parsedTime: parsedData?.time, parsedMachineCode: parsedData?.parsedMachineCode,
                  url: MOCK_DESCRIPTIONS[originalDatePath + fileName] && MOCK_DESCRIPTIONS[originalDatePath + fileName].startsWith('data:image') ? MOCK_DESCRIPTIONS[originalDatePath + fileName] : `https://picsum.photos/seed/${(originalDatePath + fileName).replace(/[^a-zA-Z0-9]/g, '')}/400/300`,
                  prefix: currentPath, descriptionTextPath: originalDatePath + fileName.replace(/\.[^/.]+$/, "") + DESCRIPTION_FILE_EXTENSION, 
                });
              });
            }
          });
          results.sort((a, b) => {
            if (a.itemType === 'file' && b.itemType === 'file') {
              const parsedA = parseS3Filename(a.name);
              const parsedB = parseS3Filename(b.name);
              if (parsedA && parsedB) return parsedB.dateTime.localeCompare(parsedA.dateTime);
            }
            return a.name.localeCompare(b.name); 
          });
          resolve(results);
          return;
        }
        
        let levelData = MOCK_S3_STRUCTURE[currentPath];
        if (levelData) {
            levelData.folders.forEach(folderNameOrKey => {
                let s3Path = currentPath + folderNameOrKey + "/";
                if (currentPath === `${GENERATED_IMAGES_FASE_PATH}/${username}/` && customFoldersParam?.some(cf => cf.name === folderNameOrKey)) return;
                results.push({ itemType: 'folder', id: s3Path, name: folderNameOrKey, path: s3Path });
            });
            levelData.files.forEach(fileName => {
                const fileId = currentPath + fileName;
                let customFolderNameFromPath: string | undefined = undefined;
                const cpParts = currentPath.split('/').filter(Boolean);
                if (cpParts.length === 4 && cpParts[0] === GENERATED_IMAGES_FASE_PATH && cpParts[1] === username) customFolderNameFromPath = cpParts[2];
                const parsedData = parseS3Filename(fileName, customFolderNameFromPath);
                results.push({
                    itemType: 'file', id: fileId, name: fileName, 
                    displayName: parsedData?.shortDisplayName || (fileName.length > 30 ? fileName.substring(0,27) + "..." : fileName),
                    parsedDate: parsedData?.date, parsedTime: parsedData?.time, parsedMachineCode: parsedData?.parsedMachineCode,
                    url: MOCK_DESCRIPTIONS[fileId] && MOCK_DESCRIPTIONS[fileId].startsWith('data:image') ? MOCK_DESCRIPTIONS[fileId] : `https://picsum.photos/seed/${fileId.replace(/[^a-zA-Z0-9]/g, '')}/400/300`,
                    prefix: currentPath, descriptionTextPath: currentPath + fileName.replace(/\.[^/.]+$/, "") + DESCRIPTION_FILE_EXTENSION,
                });
            });
        }
        results.sort((a,b) => {
            if (a.itemType === 'folder' && b.itemType === 'file') return -1;
            if (a.itemType === 'file' && b.itemType === 'folder') return 1;
            if (a.itemType === 'file' && b.itemType === 'file') {
                 const parsedA = parseS3Filename(a.name);
                 const parsedB = parseS3Filename(b.name);
                 if (parsedA && parsedB) return parsedB.dateTime.localeCompare(parsedA.dateTime);
            }
            return b.name.localeCompare(a.name);
        });
        resolve(results);
      }, 300);
    });
  } else {
    let endpoint = `${WORDPRESS_API_BASE_URL}/ai-image-studio/v1/list-items?path=${encodeURIComponent(currentPath)}&isUserGallery=${isUserGalleryMode}`;
    if (username) endpoint += `&username=${encodeURIComponent(username)}`;
    if (filterCriteria) endpoint += `&filterType=${filterCriteria.type}&filterValue=${encodeURIComponent(filterCriteria.value)}`;
    try {
      const response = await fetch(endpoint);
      if (!response.ok) throw new Error(`WordPress API Error: ${response.status}`);
      const data: S3ListItem[] = await response.json();
      return data.map(item => {
        if (item.itemType === 'file') {
          let customContext: string | undefined = undefined;
          if (isUserGalleryMode && username && item.prefix) {
            const prefixParts = item.prefix.split('/').filter(Boolean);
            if (prefixParts.length === 4 && prefixParts[0] === GENERATED_IMAGES_FASE_PATH && prefixParts[1] === username) customContext = prefixParts[2];
          }
          const parsed = parseS3Filename(item.name, customContext);
          if (!item.displayName) item.displayName = parsed?.shortDisplayName || (item.name.length > 30 ? item.name.substring(0,27) + "..." : item.name);
          if (!item.parsedDate) item.parsedDate = parsed?.date;
          if (!item.parsedTime) item.parsedTime = parsed?.time;
          if (!item.parsedMachineCode) item.parsedMachineCode = parsed?.parsedMachineCode;
        }
        return item;
      });
    } catch (error) {
      throw new Error(`Could not load items from WordPress. ${error instanceof Error ? error.message : String(error)}`);
    }
  }
};

export interface ImageMetadata { description: string; star: StarType; }

export const fetchImageMetadata = async (s3File: S3File): Promise<ImageMetadata> => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve) => {
      setTimeout(() => {
        const description = MOCK_DESCRIPTIONS[s3File.descriptionTextPath] || s3File.descriptionText || `Mock description for ${s3File.name}.`;
        const star = MOCK_STAR_DATA[s3File.id] || StarType.None;
        resolve({ description, star });
      }, 150);
    });
  } else {
    const endpoint = `${WORDPRESS_API_BASE_URL}/ai-image-studio/v1/item-metadata?id=${encodeURIComponent(s3File.id)}`;
    try {
      const response = await fetch(endpoint);
      if (!response.ok) throw new Error(`WP API Error: ${response.status}`);
      const data = await response.json();
      return { description: data?.description || "No description", star: data?.star || StarType.None };
    } catch (error) {
      throw new Error(`Error fetching metadata. ${error instanceof Error ? error.message : String(error)}`);
    }
  }
};

export const saveGeneratedImage = async (
  imageDataBase64: string,
  originalFileContext: S3File | undefined | null, 
  promptUsed: string | undefined,
  appliedEditLabel: string | undefined,
  username: string,
  customFolderName?: string
): Promise<S3File | null> => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve) => {
      setTimeout(() => {
        const today = new Date().toISOString().split('T')[0];
        const userLevelPath = `${GENERATED_IMAGES_FASE_PATH}/${username}/`;
        if (!MOCK_S3_STRUCTURE[userLevelPath]) MOCK_S3_STRUCTURE[userLevelPath] = { folders: [], files: [] };
        
        let targetFolder = userLevelPath;
        if (customFolderName) {
            targetFolder = userLevelPath + customFolderName + "/";
            if (!MOCK_S3_STRUCTURE[userLevelPath].folders.includes(customFolderName)) MOCK_S3_STRUCTURE[userLevelPath].folders.push(customFolderName);
            if (!MOCK_S3_STRUCTURE[targetFolder]) MOCK_S3_STRUCTURE[targetFolder] = { folders: [], files: [] };
        }
        
        const saveDatePath = targetFolder + today + "/";
        if (!MOCK_S3_STRUCTURE[targetFolder].folders.includes(today)) MOCK_S3_STRUCTURE[targetFolder].folders.push(today);
        if (!MOCK_S3_STRUCTURE[saveDatePath]) MOCK_S3_STRUCTURE[saveDatePath] = { folders: [], files: [] };

        const timestamp = new Date().toISOString().replace('T', ' ').substring(0,19).replace(/:/g,'-');
        const serial = Date.now().toString().slice(-5);
        const seed = Math.floor(Math.random() * 8999999).toString();
        let machine = "GenAI";
        let category = customFolderName ? customFolderName.replace(/\s+/g, '-') : "Generated"; 

        if (originalFileContext) {
            const parsed = parseS3Filename(originalFileContext.name);
            if (parsed) { machine = parsed.parsedMachineCode; if (!customFolderName) category = parsed.category; }
        }
        
        const fileName = `${category}${appliedEditLabel ? `_${appliedEditLabel.replace(/\s+/g, '-')}` : ""}_${timestamp}_${serial}-${seed}-${username}-${machine}.jpg`;
        const fullFilePath = saveDatePath + fileName;
        const descriptionPath = saveDatePath + fileName.replace(/\.[^/.]+$/, "") + DESCRIPTION_FILE_EXTENSION;

        MOCK_S3_STRUCTURE[saveDatePath].files.push(fileName);
        MOCK_STAR_DATA[fullFilePath] = StarType.None; 
        MOCK_DESCRIPTIONS[descriptionPath] = `Prompt: ${promptUsed}\nEdit: ${appliedEditLabel}`;
        const imageUrl = imageDataBase64.startsWith('data:image') ? imageDataBase64 : `data:image/jpeg;base64,${imageDataBase64}`;
        MOCK_DESCRIPTIONS[fullFilePath] = imageUrl;
        
        const parsed = parseS3Filename(fileName, customFolderName);
        resolve({
          itemType: 'file', id: fullFilePath, name: fileName, displayName: parsed?.shortDisplayName || fileName,
          parsedDate: parsed?.date, parsedTime: parsed?.time, parsedMachineCode: parsed?.parsedMachineCode,
          url: imageUrl, prefix: saveDatePath, descriptionTextPath: descriptionPath, descriptionText: MOCK_DESCRIPTIONS[descriptionPath],
        });
      }, 500);
    });
  } else {
    try {
        const response = await fetch(`${WORDPRESS_API_BASE_URL}/ai-image-studio/v1/save-generated-image`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ imageDataBase64, originalFileContextId: originalFileContext?.id, promptUsed, appliedEditLabel, username, customFolderName }),
        });
        if (!response.ok) throw new Error("Save error");
        return await response.json();
    } catch (error) { return null; }
  }
};

export const exportFinalImage = async (
  imageToExport: S3File,
  imageDataBase64: string,
  username: string, 
  exportPrompt?: string,
  exportAppliedEditLabel?: string
): Promise<S3File | null> => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve) => {
      setTimeout(() => {
        const today = new Date().toISOString().split('T')[0];
        const finalSavePath = `${EXPORTED_IMAGES_FASE_PATH}/${username}/${today}/`;
        if (!MOCK_S3_STRUCTURE[finalSavePath]) MOCK_S3_STRUCTURE[finalSavePath] = { folders: [], files: [] };

        const sourceParsed = parseS3Filename(imageToExport.name);
        const fileName = `${sourceParsed?.category || "Export"}_${new Date().toISOString().replace('T', ' ').substring(0,19).replace(/:/g,'-')}_${Date.now().toString().slice(-5)}-${Math.floor(Math.random() * 999)}-${username}-${sourceParsed?.parsedMachineCode || "Exp"}.jpg`;
        const fullFilePath = finalSavePath + fileName;
        const descPath = finalSavePath + fileName.replace(/\.[^/.]+$/, "") + DESCRIPTION_FILE_EXTENSION;

        MOCK_S3_STRUCTURE[finalSavePath].files.push(fileName);
        MOCK_STAR_DATA[fullFilePath] = StarType.None; 
        MOCK_DESCRIPTIONS[descPath] = `Exported Artwork: ${exportPrompt}`;
        const imageUrl = imageDataBase64.startsWith('data:image') ? imageDataBase64 : `data:image/jpeg;base64,${imageDataBase64}`;
        MOCK_DESCRIPTIONS[fullFilePath] = imageUrl;

        const parsed = parseS3Filename(fileName);
        resolve({
          itemType: 'file', id: fullFilePath, name: fileName, displayName: parsed?.shortDisplayName || fileName,
          parsedDate: parsed?.date, parsedTime: parsed?.time, parsedMachineCode: parsed?.parsedMachineCode,
          url: imageUrl, prefix: finalSavePath, descriptionTextPath: descPath,
        });
      }, 700);
    });
  } else {
    try {
        const response = await fetch(`${WORDPRESS_API_BASE_URL}/ai-image-studio/v1/export-final-image`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ originalFileId: imageToExport.id, imageDataBase64, username, exportPrompt, exportAppliedEditLabel }),
        });
        return await response.json();
    } catch (error) { return null; }
  }
};

export const saveImageStar = async (imageId: string, star: StarType): Promise<boolean> => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve) => {
      setTimeout(() => { MOCK_STAR_DATA[imageId] = star; resolve(true); }, 200);
    });
  } else {
    try {
      const response = await fetch(`${WORDPRESS_API_BASE_URL}/ai-image-studio/v1/set-image-star`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageId, star }),
      });
      const result = await response.json();
      return result.success === true;
    } catch (error) { return false; }
  }
};